import React, { useEffect, useState } from 'react';
import PageForm from "components/pageForm/PageForm";
import SubNavbar from "components/SubNavbar/SubNavbar";
import ApiDatabase from 'server';
import Usefetchdata from "hooks/usefetchdata";
import UseContext from "hooks/useContext";
import useModal from "components/modal/useModal";
import Chip from "components/chip/chip";
import Button from "components/button";
import ThreeStateCheckbox from "components/threeStateCheckbox/threeStateCheckbox";
import { Loading2, BanIcon, CheckIcon } from "assets/icons";

const roleLabels = {
    500: 'Admin',
    515: 'Collaborateur',
};

const roleColors = {
    500: 'WARNING',
    515: 'PRIMARY',
};

const getInitials = (firstname, lastname) => {
    const f = (firstname || '').charAt(0).toUpperCase();
    const l = (lastname || '').charAt(0).toUpperCase();
    return (f + l) || '--';
};

const AdminConsole = () => {
    const { userData } = Usefetchdata();
    const { setModalData, modalDataReturn } = UseContext();
    const { toggle } = useModal();
    const [team, setTeam] = useState([]);
    const [deactivatedMembers, setDeactivatedMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    // false = n'affiche pas les désactivés (par défaut), true = affiche seulement désactivés, null = affiche tout
    const [showDeactivated, setShowDeactivated] = useState(false);
    const token = localStorage.getItem('xsrfToken');

    const fetchTeam = () => {
        if (token) {
            ApiDatabase.getAdminTeam(
                { token },
                (data) => {
                    setTeam(data);
                    setLoading(false);
                },
                (err) => {
                    console.error(err);
                    setLoading(false);
                }
            );
        }
    };

    const fetchDeactivatedMembers = () => {
        if (token) {
            ApiDatabase.getDeactivatedAdminTeam(
                { token },
                (data) => {
                    setDeactivatedMembers(data);
                },
                (err) => {
                    console.error(err);
                }
            );
        }
    };

    useEffect(() => {
        fetchTeam();
        fetchDeactivatedMembers();
    }, [token]);

    useEffect(() => {
        if (modalDataReturn?.action === 'admin_member_deactivated' || modalDataReturn?.action === 'admin_member_reactivated') {
            fetchTeam();
            fetchDeactivatedMembers();
        }
    }, [modalDataReturn]);

    const handleDeactivate = (member) => {
        setModalData({ ...member, mode: 'deactivate' });
        toggle('admin_console_deactivate');
    };

    const handleReactivate = (member) => {
        setModalData({ ...member, mode: 'reactivate' });
        toggle('admin_console_reactivate');
    };

    // Détermine quels membres afficher selon le filtre
    const getDisplayMembers = () => {
        if (showDeactivated === true) {
            return deactivatedMembers;
        } else if (showDeactivated === false) {
            return team;
        } else {
            // null = affiche tout
            return [...team, ...deactivatedMembers];
        }
    };

    const displayMembers = getDisplayMembers();
    const totalMembers = team.length;
    const countByRole = (role) => team.filter(m => m.role === role).length;

    return (
        <PageForm>
            <SubNavbar>
                <div className="flex justify-between items-center w-full gap-3">
                    <div className="flex gap-2 items-center">
                        {/* Checkbox pour afficher les membres désactivés */}
                        <div className="border rounded px-4 h-10 flex items-center hover:bg-gray-100 transition-colors">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <ThreeStateCheckbox
                                    checked={showDeactivated}
                                    onChange={setShowDeactivated}
                                />
                                <div className="whitespace-nowrap text-sm">Membres désactivés</div>
                            </label>
                        </div>
                    </div>
                </div>
            </SubNavbar>
            <div className="w-full px-2 tab:px-4 lg:px-8 py-8 mb-28 flex flex-col gap-8">

                <div className="grid grid-cols-1 tab:grid-cols-3 gap-4 w-full">
                    <div className="bg-gray-50 border border-gray-200 flex flex-col items-start rounded-lg px-6 py-5">
                        <p className="text-sm font-medium text-gray-700">Total membres</p>
                        <p className="text-3xl font-medium text-gray-900">{loading ? '-' : totalMembers}</p>
                    </div>
                    {Object.entries(roleLabels).map(([role, label]) => (
                        <div key={role} className="bg-gray-50 border border-gray-200 flex flex-col items-start rounded-lg px-6 py-5">
                            <p className="text-sm font-medium text-gray-700">{label}</p>
                            <p className="text-3xl font-medium text-gray-900">{loading ? '-' : countByRole(Number(role))}</p>
                        </div>
                    ))}
                </div>

                <div className="w-full bg-white border border-gray-100 rounded-lg overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loading2 wh={32} color="#9CA3AF" />
                        </div>
                    ) : displayMembers.length === 0 ? (
                        <div className="flex items-center justify-center py-16">
                            <p className="text-sm text-gray-500">Aucun membre trouvé</p>
                        </div>
                    ) : (
                        <table className="table-auto w-full text-gray-500 ft-sm">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="py-3.5 pl-4 pr-2 text-left text-sm font-medium text-gray-700 bg-gray-50">MEMBRE</th>
                                    <th className="py-3.5 px-2 text-left text-sm font-medium text-gray-700 bg-gray-50">RÔLE</th>
                                    <th className="py-3.5 px-2 pr-4 text-right text-sm font-medium text-gray-700 bg-gray-50"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayMembers.map((member, index) => {
                                    const isDeactivated = member.firstname?.includes('*');
                                    const isCurrentUser = userData?.email === member.email;
                                    return (
                                        <tr key={member._id} className="hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0 motion-safe:animate-fadeUp" style={{ animationDelay: `${Math.min(index, 9) * 30}ms` }}>
                                            <td className="py-4 pl-4 pr-2">
                                                <div className="flex items-center gap-4">
                                                    <div className="min-w-[40px] w-[40px] h-[40px] rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                                        <span className="text-sm font-medium text-gray-700">
                                                            {getInitials(member.firstname, member.lastname)}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col items-start justify-center min-w-0 flex-1">
                                                        <p className="text-gray-900">
                                                            {member.firstname} {member.lastname}
                                                            {isCurrentUser && <span className="text-gray-400 ml-1">(vous)</span>}
                                                        </p>
                                                        <p className="text-gray-500 text-xs">{member.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-2">
                                                <Chip type="INDICATOR" color={roleColors[member.role] || 'PRIMARY'}>
                                                    {roleLabels[member.role] || `Rôle ${member.role}`}
                                                </Chip>
                                            </td>
                                            <td className="py-4 px-2 pr-4">
                                                <div className="flex justify-end items-center w-full">
                                                    {!isCurrentUser && !isDeactivated && member.role !== 500 && (
                                                        <Button
                                                            size="ICON"
                                                            color="SECONDARY"
                                                            onClick={() => handleDeactivate(member)}
                                                            className="border border-gray-200 ml-auto"
                                                        >
                                                            <BanIcon wh={18} color="#DC2626" />
                                                        </Button>
                                                    )}
                                                    {!isCurrentUser && isDeactivated && (
                                                        <Button
                                                            size="ICON"
                                                            color="SECONDARY"
                                                            onClick={() => handleReactivate(member)}
                                                            className="border border-gray-200 ml-auto"
                                                        >
                                                            <CheckIcon wh={18} color="#16A34A" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </PageForm>
    );
};

export default AdminConsole;
